export interface EditUndoChange {
  path: string;
  prev: any;
  next: any;
}

export interface EditUndoAction {
  ts: number;
  changes: EditUndoChange[];
}

export type EditUndoPathFilter = (path: string) => boolean;

export interface EditUndoOptions {
  getSnapshot: () => any;
  applyChange: (path: string, value: any) => void;
  setCanUndo: (canUndo: boolean) => void;
  canUndo: () => boolean;
  isPathTracked?: EditUndoPathFilter;
  /** When set, undo applies changes in this order (forward). Otherwise reverse order is used. */
  sortChangesForApply?: (changes: EditUndoChange[]) => EditUndoChange[];
}

/** Restore offer type before pricing patches so fields match the rebuilt pricing form. */
export function sortOfferEditUndoChanges(changes: EditUndoChange[]): EditUndoChange[] {
  const offerType = changes.filter((c) => c.path === 'Detail.OfferTypeId');
  const pricing = changes.filter((c) => c.path.startsWith('pricing.'));
  const rest = changes.filter(
    (c) => !offerType.includes(c) && !pricing.includes(c)
  );
  return [...offerType, ...pricing, ...rest];
}

/** Default: Detail.*, pricing.*, tag ids, and category ids (offer / offer-group). */
export function defaultEditUndoPathFilter(path: string): boolean {
  return (
    !!path &&
    (path.startsWith('Detail.') ||
      path.startsWith('pricing.') ||
      path === 'updatedOfferTagIds' ||
      path === 'productCategoryIds')
  );
}

/** Detail.* and tag id paths only (promo; no pricing). */
export function detailOnlyEditUndoPathFilter(path: string): boolean {
  return !!path && (path.startsWith('Detail.') || path === 'updatedOfferTagIds');
}

export function diffEditUndoSnapshots(
  prev: any,
  next: any,
  isPathTracked: EditUndoPathFilter
): EditUndoChange[] {
  const changes: EditUndoChange[] = [];

  const visit = (p: any, n: any, prefix: string) => {
    if (p === n) return;

    if (p instanceof Date || n instanceof Date) {
      const pTime = p instanceof Date ? p.getTime() : p;
      const nTime = n instanceof Date ? n.getTime() : n;
      if (pTime !== nTime) {
        changes.push({ path: prefix, prev: p, next: n });
      }
      return;
    }

    const pIsObj = p && typeof p === 'object';
    const nIsObj = n && typeof n === 'object';

    if (!pIsObj || !nIsObj) {
      changes.push({ path: prefix, prev: p, next: n });
      return;
    }

    if (Array.isArray(p) || Array.isArray(n)) {
      if (JSON.stringify(p) !== JSON.stringify(n)) {
        changes.push({ path: prefix, prev: p, next: n });
      }
      return;
    }

    const keys = new Set([...Object.keys(p || {}), ...Object.keys(n || {})]);
    keys.forEach((k) => {
      visit(p?.[k], n?.[k], prefix ? `${prefix}.${k}` : k);
    });
  };

  visit(prev, next, '');

  return changes.filter((c) => isPathTracked(c.path));
}

/**
 * Centralizes undo stack, snapshot diffing, and capture for detail edit screens.
 */
export class EditUndoController {
  private stack: EditUndoAction[] = [];
  private baseline: any | null = null;
  private lastCommitted: any | null = null;
  private enabled = false;

  isApplyingUndo = false;
  suppressCapture = false;

  constructor(private readonly options: EditUndoOptions) {}

  private get pathFilter(): EditUndoPathFilter {
    return this.options.isPathTracked ?? defaultEditUndoPathFilter;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearStack();
    }
  }

  get hasUndoActions(): boolean {
    return this.stack.length > 0;
  }

  clearStack(): void {
    this.stack = [];
    this.baseline = null;
    this.lastCommitted = null;
    this.options.setCanUndo(false);
  }

  runSuppressed(fn: () => void): void {
    const previous = this.suppressCapture;
    this.suppressCapture = true;
    try {
      fn();
    } finally {
      this.suppressCapture = previous;
    }
  }

  captureIfNeeded(): void {
    if (!this.enabled || this.suppressCapture) {
      return;
    }

    if (!this.baseline) {
      this.baseline = this.options.getSnapshot();
      this.lastCommitted = this.baseline;
      this.options.setCanUndo(this.options.canUndo());
      return;
    }

    const prev = this.lastCommitted;
    const next = this.options.getSnapshot();
    const changes = diffEditUndoSnapshots(prev, next, this.pathFilter);

    if (changes.length === 0) {
      this.lastCommitted = next;
      return;
    }

    this.stack.push({ ts: Date.now(), changes });
    // console.log('stack', this.stack.flatMap(s => s.changes));
    this.lastCommitted = next;
    this.options.setCanUndo(this.options.canUndo());
  }

  captureOnFieldCommit(canCapture: () => boolean): void {
    if (!this.enabled || this.isApplyingUndo || this.suppressCapture) {
      return;
    }
    if (!canCapture()) {
      return;
    }
    this.captureIfNeeded();
  }

  applyUserMutation(mutator: () => void, canCapture: () => boolean): void {
    if (!canCapture()) {
      mutator();
      return;
    }

    if (!this.baseline) {
      this.baseline = this.options.getSnapshot();
      this.lastCommitted = this.baseline;
    }

    const prev = this.lastCommitted ?? this.baseline;
    mutator();
    const next = this.options.getSnapshot();
    const changes = diffEditUndoSnapshots(prev, next, this.pathFilter);

    if (changes.length === 0) {
      this.lastCommitted = next;
      return;
    }

    this.stack.push({ ts: Date.now(), changes });
    this.lastCommitted = next;
    this.options.setCanUndo(this.options.canUndo());
  }

  undoLast(): boolean {
    if (!this.options.canUndo()) {
      return false;
    }

    const action = this.stack.pop();
    // console.log('action', action?.changes.flatMap(c => c));
    if (!action) {
      return false;
    }

    this.isApplyingUndo = true;
    try {
      const changes = this.options.sortChangesForApply
        ? this.options.sortChangesForApply(action.changes)
        : action.changes;

      if (this.options.sortChangesForApply) {
        for (const change of changes) {
          this.options.applyChange(change.path, change.prev);
        }
      } else {
        for (let i = changes.length - 1; i >= 0; i--) {
          this.options.applyChange(changes[i].path, changes[i].prev);
        }
      }
    } finally {
      this.isApplyingUndo = false;
      this.refreshSnapshotsAfterUndo();
      this.options.setCanUndo(this.options.canUndo());
    }

    return true;
  }

  refreshSnapshotsAfterUndo(): void {
    if (!this.enabled) {
      return;
    }
    const snapshot = this.options.getSnapshot();
    if (!this.baseline) {
      this.baseline = snapshot;
    }
    this.lastCommitted = snapshot;
  }

  finalizeBaselineFromLoad(): void {
    if (!this.enabled) {
      return;
    }
    this.runSuppressed(() => {
      this.clearStack();
      this.baseline = this.options.getSnapshot();
      this.lastCommitted = this.baseline;
      this.options.setCanUndo(false);
    });
    this.suppressCapture = false;
  }
}
